import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API_URLS } from "../../../api/urls";
import { EnhancedTokenManager } from "../../../utils/tokenManager";
import { SubdomainManager } from "../../../utils/subdomainUtils";
import Lottie from "lottie-react";
import DotLoader from "../../../assets/Animation/Dot Loader.json";
import { DEFAULT_FONT_FAMILY } from "../../../config/fontConfig";
import Button from "../../ui/buttons";

type Toast = {
  message: string;
  type: "error" | "info";
};

interface AuthResponse {
  message: string;
  access_token: string;
  refresh_token: string;
  token_type: string;
}

interface UserProfileResponse {
  id: number;
  email: string;
  full_name: string;
  profile_image: string | null;
  onboarded: boolean;
  workspace_id?: number;
  workspace?: {
    id: number;
    name: string;
    subdomain?: string;
    icon_url?: string;
  };
}

interface OTPComponentProps {
  email: string;
  fullName?: string;
  isSignUp: boolean;
  returnUrl?: string;
  returnState?: any;
  onBack: () => void;
}

const OTPComponent: React.FC<OTPComponentProps> = ({
  email,
  fullName = "",
  isSignUp,
  returnUrl = "",
  returnState = null,
  onBack,
}) => {
  const navigate = useNavigate();
  const fontFamily = DEFAULT_FONT_FAMILY;

  const [otp, setOtp] = useState(Array(6).fill(""));
  const [timer, setTimer] = useState(105);
  const [toast, setToast] = useState<Toast | null>(null);
  const [invalidOtp, setInvalidOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const showToast = (message: string, type: "error" | "info") => {
    setToast({ message, type });
  };

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (invalidOtp) {
      setInvalidOtp(false);
    }

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "Enter" && isOtpComplete && !invalidOtp && !loading) {
      handleVerify();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();
    const digits = pastedData.replace(/\D/g, "");

    if (digits.length === 6) {
      const newOtp = digits.split("");
      setOtp(newOtp);

      if (invalidOtp) {
        setInvalidOtp(false);
      }

      inputRefs.current[5]?.focus();
    } else if (digits.length > 0) {
      showToast("Please paste a 6-digit OTP", "error");
    }
  };

  const fetchUserProfile = async (
    accessToken: string
  ): Promise<UserProfileResponse | null> => {
    try {
      const response = await fetch(API_URLS.GET_USER_PROFILE, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
  };

  const navigateUserToCorrectLocation = async (
    userProfile: UserProfileResponse,
    isSignUp: boolean
  ) => {
    // ✅ CRITICAL FIX: Always clear subdomain data at the start
    console.log("🔄 Clearing any previous subdomain data before navigation");
    SubdomainManager.clearWorkspaceSubdomain();
    
    // Also clear any cached workspace data for ALL users
    try {
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (key.includes('workspace_subdomain') || 
            key.includes('workspace_id_user_') ||
            key.includes('workspace_data_user_')) {
          localStorage.removeItem(key);
          console.log(`🧹 Cleared old key: ${key}`);
        }
      });
    } catch (error) {
      console.warn("Failed to clear old workspace data:", error);
    }

    // Small delay to ensure cleanup completes
    await new Promise(resolve => setTimeout(resolve, 50));

    if (
      returnUrl &&
      returnUrl.includes("/view-card/") &&
      userProfile.onboarded
    ) {
      const cardIdMatch = returnUrl.match(/\/view-card\/(\d+)/);
      const cardId = cardIdMatch ? parseInt(cardIdMatch[1]) : null;

      if (cardId) {
        navigate("/shared-feedback-view", {
          replace: true,
          state: {
            cardId: cardId,
            fromDeepLink: true,
            returnToDashboard: true,
            developerEmail:
              returnState?.developerEmail ||
              localStorage.getItem("last_developer_email"),
            activeTab: "all",
            fromAuth: true,
          },
        });
        return;
      }
    }

    if (isSignUp || !userProfile.onboarded) {
      navigate("/onboarding", {
        replace: true,
        state: {
          email: userProfile.email,
          fullName: userProfile.full_name,
          fromSignUp: isSignUp,
          returnUrl: returnUrl,
          returnState: returnState,
        },
      });
      return;
    }

    if (userProfile.onboarded) {
      if (!userProfile.workspace?.subdomain && userProfile.workspace_id) {
        try {
          const accessToken = EnhancedTokenManager.getAccessToken();
          if (accessToken) {
            const response = await fetch(API_URLS.GET_WORKSPACES, {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            });

            if (response.ok) {
              const data = await response.json();
              if (data.workspaces && data.workspaces.length > 0) {
                const workspace = data.workspaces[0];

                if (workspace.subdomain) {
                  userProfile.workspace = {
                    id: workspace.id,
                    name: workspace.name,
                    subdomain: workspace.subdomain,
                    icon_url: workspace.icon_url,
                  };

                  EnhancedTokenManager.setWorkspaceDataForUser(
                    userProfile.id,
                    workspace
                  );
                }
              }
            }
          }
        } catch (error) {
          console.error("Failed to fetch workspace details:", error);
        }
      }

      if (userProfile.workspace && userProfile.workspace.subdomain) {
        const extractedSubdomain = SubdomainManager.extractSubdomainFromUrl(
          userProfile.workspace.subdomain
        );

        if (extractedSubdomain) {
          // ✅ Double-check: Clear one more time before storing new subdomain
          SubdomainManager.clearWorkspaceSubdomain();
          
          // Small delay to ensure clear completes
          await new Promise(resolve => setTimeout(resolve, 50));
          
          // Now store the NEW user's subdomain
          SubdomainManager.storeWorkspaceSubdomain(extractedSubdomain);
          console.log(`✅ New user subdomain stored: ${extractedSubdomain}`);

          if (userProfile.id) {
            EnhancedTokenManager.setWorkspaceDataForUser(
              userProfile.id,
              userProfile.workspace
            );
          }

          if (SubdomainManager.isLocalDevelopment()) {
            navigate("/dashboard", {
              replace: true,
              state: {
                fromSignIn: true,
                workspaceId: userProfile.workspace_id,
                userId: userProfile.id,
              },
            });
            return;
          }

          // ✅ Add a small delay to ensure storage is written
          setTimeout(() => {
            SubdomainManager.navigateToSubdomain(
              extractedSubdomain,
              "/dashboard",
              true
            );
          }, 100);
          return;
        }
      }

      try {
        const accessToken = EnhancedTokenManager.getAccessToken();
        if (accessToken) {
          const response = await fetch(API_URLS.GET_WORKSPACES, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.workspaces && data.workspaces.length > 0) {
              const workspace = data.workspaces[0];

              if (workspace.subdomain) {
                const extractedSubdomain =
                  SubdomainManager.extractSubdomainFromUrl(workspace.subdomain);

                if (extractedSubdomain) {
                  // ✅ Clear before storing
                  SubdomainManager.clearWorkspaceSubdomain();
                  await new Promise(resolve => setTimeout(resolve, 50));
                  
                  SubdomainManager.storeWorkspaceSubdomain(extractedSubdomain);
                  console.log(`✅ Fetched subdomain stored: ${extractedSubdomain}`);
                  
                  EnhancedTokenManager.setWorkspaceDataForUser(
                    userProfile.id,
                    workspace
                  );

                  if (SubdomainManager.isLocalDevelopment()) {
                    navigate("/dashboard", {
                      replace: true,
                      state: {
                        fromSignIn: true,
                        workspaceId: workspace.id,
                        userId: userProfile.id,
                      },
                    });
                    return;
                  }

                  // ✅ Add delay for storage to complete
                  setTimeout(() => {
                    SubdomainManager.navigateToSubdomain(
                      extractedSubdomain,
                      "/dashboard",
                      true
                    );
                  }, 100);
                  return;
                }
              }
            }
          }

          throw new Error("Could not fetch workspace subdomain");
        }
      } catch (error) {
        console.error("Failed to fetch workspace details:", error);

        if (SubdomainManager.isLocalDevelopment()) {
          navigate("/dashboard", {
            replace: true,
            state: {
              fromSignIn: true,
              workspaceId: userProfile.workspace_id,
              userId: userProfile.id,
            },
          });
        } else {
          const appDomain = `app.${SubdomainManager.getBaseDomain()}`;
          window.location.replace(`https://${appDomain}/dashboard`);
        }
      }
      return;
    }

    navigate("/onboarding", {
      replace: true,
      state: {
        email: userProfile.email,
        fullName: userProfile.full_name,
        fromSignIn: true,
      },
    });
  };

  const handleVerify = async () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 6) {
      showToast("Please enter complete OTP", "error");
      return;
    }

    setLoading(true);
    try {
      const apiUrl = isSignUp
        ? API_URLS.SIGNUP_VERIFY_REQUEST
        : API_URLS.SIGNIN_VERIFY_REQUEST;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          otp: enteredOtp,
        }),
      });

      if (response.ok) {
        const data: AuthResponse = await response.json();

        if (data.access_token && data.refresh_token) {
          // ✅ CRITICAL FIX: Clear ALL old user data before setting new tokens
          console.log("🧹 Clearing all previous user data before login");
          
          // Clear subdomain first
          SubdomainManager.clearWorkspaceSubdomain();
          
          // Clear old tokens
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("current_user_id");
          
          // Clear all workspace data for ALL users
          try {
            const allKeys = Object.keys(localStorage);
            allKeys.forEach(key => {
              if (key.includes('workspace_id_user_') ||
                  key.includes('user_profile_user_') ||
                  key.includes('workspace_data_user_') ||
                  key.includes('workspace_icon_user_')) {
                localStorage.removeItem(key);
              }
            });
          } catch (error) {
            console.warn("Failed to clear old workspace data:", error);
          }
          
          // Small delay to ensure storage is cleared
          await new Promise(resolve => setTimeout(resolve, 50));
          
          // Now set new tokens
          EnhancedTokenManager.setTokens({
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            token_type: data.token_type || "bearer",
          });

          const userProfile = await fetchUserProfile(data.access_token);

          if (userProfile) {
            EnhancedTokenManager.setUserProfile(userProfile);

            if (userProfile.workspace && userProfile.workspace.id) {
              EnhancedTokenManager.setWorkspaceDataForUser(
                userProfile.id,
                userProfile.workspace
              );
              EnhancedTokenManager.setWorkspaceIdForUser(
                userProfile.id,
                userProfile.workspace.id
              );
            } else if (userProfile.workspace_id) {
              EnhancedTokenManager.setWorkspaceIdForUser(
                userProfile.id,
                userProfile.workspace_id
              );
            }

            window.dispatchEvent(new CustomEvent("auth-state-changed"));
            window.dispatchEvent(new CustomEvent("user-profile-updated"));

            navigateUserToCorrectLocation(userProfile, isSignUp);
          }
        }
      } else {
        const errorData = await response.json();
        setInvalidOtp(true);
        showToast(
          errorData.message || errorData.detail || "Invalid OTP",
          "error"
        );
        setLoading(false);
      }
    } catch (error) {
      console.error("Network error:", error);
      setInvalidOtp(true);
      showToast("Network error. Please try again.", "error");
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const apiUrl = isSignUp
        ? API_URLS.SIGNUP_REQUEST
        : API_URLS.LOGIN_REQUEST;

      const requestBody = isSignUp
        ? { full_name: fullName, email: email }
        : { email: email };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        setOtp(Array(6).fill(""));
        setTimer(105);
        setInvalidOtp(false);
        inputRefs.current[0]?.focus();
        showToast("OTP resent successfully", "info");
      } else {
        const errorData = await response.json();
        showToast(
          errorData.message ||
            errorData.detail ||
            "Failed to resend OTP. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Network error:", error);
      showToast("Network error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const formattedTime = `${String(Math.floor(timer / 60)).padStart(
    2,
    "0"
  )}:${String(timer % 60).padStart(2, "0")}`;
  const isOtpComplete = otp.every((digit) => digit !== "");

  return (
    <>
      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
          <div
            className={`rounded-lg shadow-lg p-4 flex items-start gap-2 max-w-sm ${
              toast.type === "error"
                ? "bg-red-50 border border-red-200"
                : "bg-blue-50 border border-blue-200"
            }`}
            style={{ fontFamily }}
          >
            <div className="flex-1">
              <p
                className={`text-sm font-medium ${
                  toast.type === "error" ? "text-red-800" : "text-blue-800"
                }`}
                style={{ fontFamily }}
              >
                {toast.message}
              </p>
            </div>
            <button
              onClick={() => setToast(null)}
              className={`transition-colors ${
                toast.type === "error"
                  ? "text-red-400 hover:text-red-600"
                  : "text-blue-400 hover:text-blue-600"
              }`}
            >
              <span className="text-xl leading-none">×</span>
            </button>
          </div>
        </div>
      )}

      {/* OTP Content */}
      <div>
        <h1
          className="text-3xl font-bold text-[#1F2937] -mb-1"
          style={{ fontFamily }}
        >
          Verify your account
        </h1>
        <p className="text-[#6B7280] mb-6 text-[16px]" style={{ fontFamily }}>
          We sent a verification code to email{" "}
          <span className="font-medium text-[#1F2937]">{email}</span>
        </p>

        <div>
          {/* OTP Input */}
          <div className="flex justify-start gap-3 mb-6" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <React.Fragment key={index}>
                <input
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={loading}
                  className={`w-14 h-14 text-center text-xl font-inter rounded-lg border-[0.5px] transition-colors ${
                    invalidOtp
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : "border-[#ADAFB7] focus:ring-[#7ABFFF] focus:border-transparent"
                  } focus:outline-none focus:ring-1 ${
                    loading
                      ? "opacity-50 cursor-not-allowed bg-gray-50"
                      : "bg-white"
                  }`}
                  autoComplete="one-time-code"
                />
                {index === 2 && (
                  <div className="w-5 h-14 flex items-center justify-center text-xl font-bold text-[#D1D1D6]">
                    -
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {invalidOtp && (
            <p className="text-red-500 text-sm mb-4" style={{ fontFamily }}>
              Invalid OTP. Please try again.
            </p>
          )}

          {/* Verify Button */}
          <Button
            variant={isOtpComplete && !invalidOtp ? "primary" : "secondary"}
            className="w-full font-semibold py-3 mb-6"
            style={{ fontFamily }}
            disabled={!isOtpComplete || invalidOtp}
            onClick={handleVerify}
          >
            {loading ? (
              <span
                className="inline-block relative"
                style={{ width: 40, height: 16 }}
              >
                <Lottie
                  animationData={DotLoader}
                  loop={true}
                  style={{
                    width: 120,
                    height: 48,
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              </span>
            ) : (
              "Verify"
            )}
          </Button>

          {/* Resend OTP */}
          <div className="text-center mb-4">
            <p className="text-[#030507] text-[16px]" style={{ fontFamily }}>
              Didn't receive OTP?{" "}
              {timer === 0 && !loading ? (
                <button
                  onClick={handleResend}
                  className="text-[#4D98DD] font-medium text-[16px]"
                  style={{ fontFamily }}
                >
                  Resend
                </button>
              ) : (
                <>
                  <span className="text-[#6B7280]">Resend</span>
                  {timer > 0 && (
                    <span className="ml-1 text-[#4D98DD] font-medium">
                      {formattedTime}
                    </span>
                  )}
                </>
              )}
            </p>
          </div>

          {/* Back to email */}
          <div className="text-center">
            <button
              onClick={onBack}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              style={{ fontFamily }}
              disabled={loading}
            >
              ← Back to email
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OTPComponent;